/**
 * PRIVATE MODE — the demo redaction switch.
 *
 * Sideguide's admin panel is, by its nature, a screen full of other people's names,
 * faces, words and money. That makes it unrecordable: there is no view of the product
 * that is not also a view of one community's private membership. This is the switch
 * that makes it recordable — one keystroke smears every identifying value on screen
 * while leaving the product itself legible, so the panel can be demoed publicly
 * without a redaction pass in post.
 *
 * SUPERADMIN ONLY, and only as a matter of who is offered it: this is a cosmetic
 * client-side filter, not an access control. It hides nothing from the person driving
 * the browser — the values are still in the DOM, still on the wire, still in
 * `localStorage`. It is for a CAMERA, not for an adversary, and nothing in this module
 * should ever be relied on as though it were the latter. The gate on the toggle
 * (`AdminShell`) exists so creators are not offered a control that would only confuse
 * them, not because engaging it would leak anything.
 *
 * HOW IT WORKS, AND WHY IT IS CSS AND NOT STATE
 * ---------------------------------------------
 * Toggling sets one attribute on `<html>`. One rule in `app.css` then blurs every
 * element carrying `.redact`. That is the whole mechanism, and the alternative is
 * worth stating because it is the obvious one and it is much worse: threading a
 * `private` boolean through props, or reading a store in every component, would put a
 * reactive dependency in ~150 places, re-render half the app on toggle, and make every
 * future component's correctness depend on somebody remembering. Here the toggle costs
 * one attribute write, zero re-renders, and a component opts in with a class name.
 *
 * WHY MARKING IS OPT-IN, WHICH IS THE UNCOMFORTABLE PART
 * ------------------------------------------------------
 * The safe design would be the inverse — blur EVERYTHING, then un-blur the chrome that
 * is known to be safe — because it fails closed: a page written next month is redacted
 * before anyone thinks about it. That design is not available. `filter` rasterises the
 * element and all of its descendants into one layer, so a blurred parent CANNOT have a
 * sharp child; there is no CSS un-blur. Every technique that blurs a container loses
 * the ability to make an exception inside it. So marking has to be opt-in.
 *
 * Opt-in fails OPEN — an unmarked value renders in the clear — and the failure lands
 * on camera, which is the worst place for it. Two things carry that weight instead of
 * a comment asking people to be careful:
 *
 *   1. MARK AT THE PRIMITIVE, NOT AT THE CALL SITE. `<Avatar>` is 53 call sites and
 *      one file. `<StatCard>` is every KPI figure on the dashboard and one file. The
 *      shared components in `@ui` and `lib/ui` carry the marks, so the great majority
 *      of identifying values on screen are redacted by virtue of being rendered the
 *      normal way. A page has to go OUT of its way to render an unredacted name.
 *   2. `scripts/check-privacy-marks.mjs`, wired into `npm run check`. It reads every
 *      `.svelte` file, finds markup that renders a known-identifying expression
 *      (`.name`, `.email`, `fmtUsd(`, `_cents`, …) without a mark, and fails the
 *      build. An intentional exception carries `privacy-ok: <reason>` — a decision
 *      someone wrote down, not a silent hole. This is the same shape as
 *      `check-community-effects.mjs`, for the same reason: the rule that is not
 *      enforced is the rule that is already broken somewhere.
 *
 * WHAT COUNTS AS IDENTIFYING
 * ---------------------------
 * The policy the marks implement, stated once so the checker and a human reviewer are
 * reading the same rule. BLUR anything that is, or narrows to, a real person, a real
 * community, or a real quantity of money or members:
 *
 *   * people — names, @handles, emails, avatars, profile links, bios
 *   * their words — post titles and bodies, comments, DM messages, AI summaries that
 *     quote them, and any media they attached
 *   * communities — name, logo, slug, Skool URL
 *   * money — MRR, revenue, at-risk and churned figures, prices, payouts, commissions
 *
 * COUNTS ARE NOT REDACTED. Post and comment totals, headcounts, upvotes, activity
 * figures, chart readouts, queue badges — all of it stays legible. This is the one
 * rule that reversed after the first build, and the reason is worth keeping: a
 * dashboard demo whose numbers are all smeared is not a demo of an analytics product.
 * The numbers are the thing being sold. Money is the exception because a community's
 * revenue is the one figure that is commercially somebody else's business.
 *
 * That split is why `<StatCard>` takes a `redact` prop instead of always redacting:
 * three of its call sites render `fmtUsd(...)` and the rest render counts, so the
 * decision belongs to the caller, who knows which it is passing.
 *
 * KEEP SHARP everything that describes the PRODUCT rather than the customer: labels,
 * headings, navigation, buttons, empty states, role and stage names, status pills,
 * icons, dates and relative times, and every chart — shape AND readouts. Dates stay
 * for the same reason counts do: they identify nobody, and without them a trend has
 * no scale at all.
 *
 * MARK LEAF ELEMENTS, NOT LAYOUT CONTAINERS. `filter` creates a stacking context and a
 * containing block for fixed/absolute descendants, so marking a wrapper that holds a
 * dropdown, tooltip or popover moves it. Put `.redact` on the span or the img that
 * carries the value. `SkoolContent` is the deliberate exception — free prose has no
 * inner leaf to mark — and it holds nothing positioned.
 *
 * WHAT IS STILL READABLE, AND WHY. Three things stay in the clear on purpose, because
 * blurring them would cost the demo more than it protects:
 *
 *   * DATES and relative times ("renews Aug 17", "13d ago"). They identify nobody, and
 *     a trend with no time axis has no scale at all.
 *   * CHARTS, whole — lines, areas, bars, heatmap cells, the activity strip, and the
 *     tooltip readouts that go with them. The only figure a chart can carry that is
 *     redacted is a money one, and those live on the revenue tiles.
 *   * PRODUCT VOCABULARY: role and stage names, plan states ("Free", "Comped"),
 *     segment labels, pipeline and category names, status pills. These describe
 *     Sideguide, not the customer.
 *   * EVERY COUNT — see above.
 *
 * A PLACEHOLDER gets its own treatment, `.redact-hint`, which hides it rather than
 * blurring it. Blurring an input blurs what you TYPE into it, and typing into the
 * composer is a thing a demo does on purpose.
 *
 * TWO LEAKS THIS CANNOT CLOSE, both outside the page:
 *
 *   1. A NATIVE `<select>` POPUP. The closed control blurs like anything else, but the
 *      option list Chrome draws when it opens is an OS window with no CSS. The Auto
 *      DMs sender picker is the one place this bites — don't open it on camera.
 *   2. THE STATUS-BAR URL. Hovering a Skool profile link shows its href, handle and
 *      all, in the browser's bottom-left bubble. Parking `href` the way `title` is
 *      parked would fix it and would also break every link in the panel, so the
 *      trade was left un-taken. Don't hover the profile links.
 *
 * And the standing caveat: this is a filter over real values, not a substitution of
 * fake ones. The DOM, the network tab and `localStorage` all still hold the truth. It
 * defeats a camera, and it is not meant to defeat anything else.
 */

const KEY = 'sg_private_mode';
const ATTR = 'data-redacted';

/**
 * Whether the redaction is engaged. Read it for the TOGGLE'S OWN state — the eye
 * icon, its tooltip, the recording indicator — and nothing else. No component should
 * branch on this to decide what to render: the point of the CSS mechanism is that a
 * component renders the same markup either way. A component that reads this is a
 * component that will re-render on toggle, which is exactly what this design bought
 * its way out of.
 */
export const privacy = $state<{ on: boolean }>({ on: read() });

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    // A browser with storage disabled just starts un-redacted; the toggle still works
    // for the session, it simply will not survive a reload.
    return false;
  }
}

/**
 * Push the current value at the document. Idempotent, and the ONLY writer of the
 * attribute — `app.css` keys off it and nothing else should set it.
 */
function apply(on: boolean): void {
  const el = document.documentElement;
  if (on) el.setAttribute(ATTR, '');
  else el.removeAttribute(ATTR);
}

// ---------------------------------------------------------------------------
// Native tooltips — the one leak CSS cannot reach
// ---------------------------------------------------------------------------
//
// A `title` attribute renders as an OS tooltip, outside the page, outside the
// stylesheet, and there is no selector that touches it. Forty of them in this app
// interpolate live data — "Open Marcus Webb on Skool", "Mark $482.00 paid" — so
// hovering a redacted row would pop the exact value out of the blur, in a floating
// box, on camera. It is the sharpest possible failure of this feature.
//
// Rather than audit those forty and re-audit them forever, the mode SUPPRESSES EVERY
// NATIVE TOOLTIP while it is on: `title` is parked in `data-sg-title` and restored on
// lift. That needs no marks, covers tooltips written next month, and fails closed —
// the exact properties the CSS half cannot have. Losing "Rename label" for the length
// of a demo is not a cost worth reasoning about.
//
// The observer is what makes it hold. Svelte re-renders constantly, and every fresh
// node arrives carrying its `title` from the template, so a one-shot sweep would
// protect only the nodes that happened to exist at the moment of the toggle. It runs
// ONLY while the mode is on.

const PARKED = 'data-sg-title';
let observer: MutationObserver | null = null;

function park(root: ParentNode): void {
  for (const el of root.querySelectorAll('[title]')) {
    el.setAttribute(PARKED, el.getAttribute('title') ?? '');
    el.removeAttribute('title');
  }
}

function unpark(): void {
  for (const el of document.querySelectorAll(`[${PARKED}]`)) {
    el.setAttribute('title', el.getAttribute(PARKED) ?? '');
    el.removeAttribute(PARKED);
  }
}

function watchTitles(on: boolean): void {
  observer?.disconnect();
  observer = null;
  if (!on) {
    unpark();
    return;
  }
  park(document);
  observer = new MutationObserver((records) => {
    for (const r of records) {
      // An attribute record fires for our OWN `title` removal too; re-running `park`
      // on an element that no longer has one is a no-op, so no guard is needed.
      if (r.type === 'attributes' && r.target instanceof Element) {
        const el = r.target;
        if (el.hasAttribute('title')) {
          el.setAttribute(PARKED, el.getAttribute('title') ?? '');
          el.removeAttribute('title');
        }
      }
      for (const node of r.addedNodes) {
        if (node instanceof Element) {
          if (node.hasAttribute('title')) {
            node.setAttribute(PARKED, node.getAttribute('title') ?? '');
            node.removeAttribute('title');
          }
          park(node);
        }
      }
    }
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['title'],
  });
}

/**
 * Engage or lift the redaction, persisting the choice.
 *
 * PERSISTED DELIBERATELY. A demo is not one page load: reloading mid-recording, or
 * following a link that remounts the app, must not silently drop the blur and put a
 * screen of real names on camera. The cost of that asymmetry is that the mode is
 * sticky across sessions, which the header indicator exists to make obvious.
 */
export function setPrivate(on: boolean): void {
  privacy.on = on;
  apply(on);
  watchTitles(on);
  try {
    if (on) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    /* ignore — the mode is still live for this page load */
  }
}

export function togglePrivate(): void {
  setPrivate(!privacy.on);
}

/**
 * Restore the persisted mode on boot, and bind the shortcut.
 *
 * Called once from `main.ts`, BEFORE the app mounts — not from a component's
 * `$effect`. If the attribute landed a frame after the first paint, a reload with the
 * mode on would flash one unredacted frame of the real panel, which on a recording is
 * indistinguishable from never having had the feature. Setting it during module
 * evaluation means the first paint is already redacted.
 *
 * THE SHORTCUT IS THE POINT OF THE SHORTCUT. Reaching for a header button means
 * pointing a cursor at the thing you are about to un-blur, on camera, while the panel
 * is still in the clear. `Ctrl/Cmd + Shift + B` is reachable blind, mid-sentence,
 * without moving the mouse. It is bound at the document, capture-phase, so it fires
 * over a focused input too.
 */
export function initPrivacy(): void {
  apply(privacy.on);
  // Nothing is mounted yet, so the sweep finds nothing — the OBSERVER is the point:
  // it is already watching when Svelte paints the first node carrying a `title`.
  watchTitles(privacy.on);
  document.addEventListener(
    'keydown',
    (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'B' || e.key === 'b')) {
        e.preventDefault();
        togglePrivate();
      }
    },
    true,
  );
}
